import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { CardComponent, CardRow } from '../../../components/Layouts/Card.styles';
import { ChartMultitype, Settings } from '@carbon/icons-react';
import Typography from "../../../components/Basics/Typography";
import Card from '../../../components/Layouts/Card';
import { Stack, NumberInput, Button, InlineLoading, Select, SelectItem } from "@carbon/react";
import { spacing05 } from "@carbon/themes";
import { Separator } from "../../Dashboard/Dashboard.styles";
import { gray10 } from '@carbon/colors';
import { Save } from '@carbon/icons-react';
import LineGraph from '../../../components/Graphs/LineChart.container';
import FuelTable from "./FuelTable.container";
import { onGetFuelThunk, onAddFuelThunk } from './TankerTruck.actions';
import LoadingContent from "../../../components/Animations/LoadingContent";

const CAMIONES = [
  { patent: 'SZBX30', device_id: 865413054103733, capacidad: 20000 },
  { patent: 'SZBK98', device_id: 865326062124790, capacidad: 13000 },
];

const TankerTruckContainer = () => {
  const dispatch = useDispatch();
  const { eventosPorCamion, remanentePorCamion, loading_fueles, loading_add_fuel } =
    useSelector(state => state.Tanker) ?? {};

  const [showRegisterPanel, setShowRegisterPanel] = useState(false);
  const [litrosCarga, setLitrosCarga] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedPatent, setSelectedPatent] = useState('');

  useEffect(() => {
    CAMIONES.forEach(c => dispatch(onGetFuelThunk(c.patent)));

    const fuelInterval = setInterval(() => {
      CAMIONES.forEach(c => dispatch(onGetFuelThunk(c.patent)));
    }, 480000); // 8 minutos

    return () => clearInterval(fuelInterval);
  }, [dispatch]);

  const handleLitrosChange = (e, { value }) => {
    const parsed = Number(value);
    setLitrosCarga(parsed);
    setHasUnsavedChanges(parsed > 0);
  };

  const handleRegistrarCarga = async () => {
    if (litrosCarga <= 0 || !selectedPatent) return;
    const camion = CAMIONES.find(c => c.patent === selectedPatent);
    await dispatch(onAddFuelThunk(litrosCarga, camion.patent, camion.device_id));
    setHasUnsavedChanges(false);
    setLitrosCarga(0);
    setSelectedPatent('');
    dispatch(onGetFuelThunk(camion.patent));
  };

  const toSantiago = (isoStr) => {
    if (!isoStr || isoStr === '-') return '-';
    return new Date(isoStr).toLocaleString('es-CL', {
      timeZone: 'America/Santiago',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <Stack gap={3}>
      <CardComponent width="100%">
        {/* ── Header ── */}
        <Card justifyContent="space-between" alignItems="center">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ChartMultitype size={18} />
            <Typography className="cds--type-heading-compact-01">
              MONITOREO NIVEL DE COMBUSTIBLE - CAMIONES COMBUSTIBLE
            </Typography>
          </div>
          <button
            onClick={() => setShowRegisterPanel(prev => !prev)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: gray10 }}
          >
            <Settings size={18} />
            <Typography className="cds--type-body-compact-01">
              Registrar carga a la cisterna del camión
            </Typography>
          </button>
        </Card>

        {/* ── Panel registrar carga ── */}
        {showRegisterPanel && (
          <Card background="unset" padding="12px" flexDirection="column" gap="12px">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <Typography className="cds--type-body-compact-01">
                Seleccione el camión combustible al que se le está cargando la cisterna y defina la cantidad de combustible con la que se cargó la cisterna del camión
              </Typography>
              {loading_add_fuel ? (
                <InlineLoading description="Guardando..." />
              ) : (
                <Button
                  kind="primary"
                  size="sm"
                  renderIcon={Save}
                  onClick={handleRegistrarCarga}
                  disabled={!hasUnsavedChanges || litrosCarga <= 0 || !selectedPatent}
                >
                  Registrar carga
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
              <div style={{ flexShrink: 0, width: '220px' }}>
                <Select
                  id="select-camion"
                  labelText="Camión combustible"
                  value={selectedPatent}
                  onChange={e => setSelectedPatent(e.target.value)}
                >
                  <SelectItem value="" text="Seleccione un camión" />
                  {CAMIONES.map(c => (
                    <SelectItem key={c.patent} value={c.patent} text={c.patent} />
                  ))}
                </Select>
              </div>
              <div style={{ flex: 1 }}>
                <NumberInput
                  id="litros-carga"
                  label="(L) Combustible"
                  value={litrosCarga}
                  onChange={handleLitrosChange}
                  min={0}
                  step={100}
                />
              </div>
            </div>
            {hasUnsavedChanges && (
              <Typography className="cds--type-helper-text-01" style={{ color: '#fa4d56' }}>
                Tienes cambios sin guardar
              </Typography>
            )}
          </Card>
        )}

        {/* ── Contenido principal ── */}
        {loading_fueles ? (
          <LoadingContent />
        ) : (
          <Stack gap={3}>
            {CAMIONES.map(camion => {
              const eventos = eventosPorCamion?.[camion.patent] ?? [];
              const remanente = remanentePorCamion?.[camion.patent] ?? 0;
              const lineData = eventos
                .filter(e => e.inicio != null && e.combustible_remanente != null)
                .sort((a, b) => new Date(a.inicio) - new Date(b.inicio))
                .map(e => ({
                  group: 'Remanente',
                  key: toSantiago(e.inicio),
                  value: Number(e.combustible_remanente),
                }));

              return (
                <CardRow key={camion.patent} width="inherit">
                  <Card
                    flexDirection="column"
                    justifyContent="center"
                    height="100%"
                    padding={spacing05}
                    width="25%"
                    alignItems="stretch"
                  >
                    <Typography className='cds--type-heading-compact-04'>{camion.patent}</Typography>
                    <Typography className='cds--type-heading-compact-04'>Combustible remanente</Typography>
                    <Typography className='cds--type-heading-07'>
                      {remanente?.toLocaleString('es-CL')} L
                    </Typography>
                    <Typography
                      className='cds--type-heading-07'
                      style={{ color: remanente / camion.capacidad < 0.2 ? '#fa4d56' : '#42be65' }}
                    >
                      {Math.min(100, Math.max(0, (remanente / camion.capacidad) * 100)).toFixed(1)} %
                    </Typography>
                    <Separator />
                    {lineData.length > 0 ? (
                      <LineGraph
                        title="Combustible Rem..."
                        lineData={lineData}
                        xAxisTitle="Tiempo"
                        yAxisTitle="Litros"
                        height="15rem"
                        showXTicks={false}
                      />
                    ) : (
                      <LineGraph title="Sin datos" lineData={[]} />
                    )}
                  </Card>
                  <FuelTable patent={camion.patent} />
                </CardRow>
              );
            })}
          </Stack>
        )}
      </CardComponent>
    </Stack>
  );
};

export default TankerTruckContainer;